import { Injectable } from '@angular/core';
import {
  asyncScheduler,
  BehaviorSubject,
  catchError,
  firstValueFrom,
  Observable,
  observeOn,
  Subject,
  Subscription,
  throwError,
  timeout,
  TimeoutError
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { MsalService } from '@azure/msal-angular';
import { HistoryService } from './history.service';
import { WidgetService } from './widget.service';
import { SelectedsystemService } from './selectedsystem.service';
import { ChatMessage, NONE_DEVICE_SPECIFIC } from '../types/chat.types';
import { HttpClient } from '@angular/common/http';
import { SessionService } from './session.service';
import { SubscriptionService } from './subscription.service';
import { AppConfigService } from './appconfig.service';
import { SelectedContentService } from './selectedcontent.service';
import { SelectedSiteService } from './selectedsite.service';
import { LanguageService } from './language.service';
import { TranslocoService } from '@jsverse/transloco';

// Helper interfaces for clarity
interface ChatProduct {
  family: string;
  apiversion: string;
  botInformation: string;
  aiSafetyInformation: string;
}
interface StreamState {
  result: string;
  final_answer: boolean;
  options: any[];
  suggestions?: string[];
  reasoningCategory?: string;
  reasoningResult?: string;
}

// Maps internal language codes to the ISO codes expected by the chat API
const LANG_ISO_MAP: Record<string, string> = {
  en: 'en',
  zh: 'zh-Hans',
  ja: 'ja',
  pt: 'pt',
  es: 'es'
};

@Injectable({
  providedIn: 'root'
})
export class ChatService {

  messages = new BehaviorSubject<ChatMessage[]>([]); //Contains all chat messages of conversation
  autoscroll = new BehaviorSubject<boolean>(false); //Enables or disables the autscroll
  doAScroll = new Subject<void>(); //Executes a scroll in the component
  isStreaming = new BehaviorSubject<boolean>(false); //Defines if streaming happens right now or not
  errorSavingChat = new BehaviorSubject<boolean>(false);
  errorStreamingChat = new BehaviorSubject<boolean>(false);
  tempVariable: any = '';
  tempVar: any = '';
  showInstrumentOptions = new BehaviorSubject<boolean>(false);
  ai_response_info: string;
  finalAnswer: boolean;

  // --- Streaming state ---
  private currentSseEvent: string | null = null; // tracks current SSE event type
  private abortController: AbortController | null = null; // controls fetch/SSE abortion
  private wasInterruptedByUser = false; // indicates user-triggered interruption
  private lineBuffer = ''; // buffers incomplete lines across chunk boundaries
  private invokeSub: Subscription | null = null; // tracks active invoke request

  constructor(
    private msalAuthService: MsalService,
    private historyService: HistoryService,
    private authService: AuthService,
    private widgetService: WidgetService,
    private selectedSystemService: SelectedsystemService,
    private http: HttpClient,
    private sessionService: SessionService,
    private subscriptionService: SubscriptionService,
    private appconfigService: AppConfigService,
    private selectedContentService: SelectedContentService,
    private selectedSiteService: SelectedSiteService,
    private languageService: LanguageService,
    private translocoService: TranslocoService
  ) {
    this.languageService.currentLanguage$.subscribe(() => {
      this.refreshLocalizedMessageMetadata();
    });

    (this.appconfigService as any).config$?.subscribe(() => {
      this.refreshLocalizedMessageMetadata();
    });
  }

  /** Stream response from backend (Server-Sent Events style). */
  streamResponse(body: any, version: string): Observable<StreamState> {
    const url = this.buildApiUrl('/data/streamChat');
    this.finalAnswer = false; // reset state at stream start
    this.wasInterruptedByUser = false; // reset interruption flag
    this.lineBuffer = ''; // reset line buffer for new stream

    return new Observable<StreamState>(observer => {
      (async () => {
        try {
          // Create a new AbortController for this stream
          this.abortController = new AbortController();

          const res = await this.fetchWithToken(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: this.abortController.signal
          });

          const reader = res.body?.getReader();
          if (!reader) {
            observer.error('No response body');
            return;
          }

          const decoder = new TextDecoder();
          const state: StreamState = { result: '', final_answer: false, options: [] };

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });

            // Buffer incomplete lines across chunk boundaries
            const lines = (this.lineBuffer + chunk).split('\n');
            // Last element might be incomplete if chunk didn't end with \n
            this.lineBuffer = lines.pop() || '';

            for (const line of lines) {
              this.parseStreamLine(line, state, observer);
            }
          }

          // Process any remaining buffered content after stream ends
          if (this.lineBuffer) {
            this.parseStreamLine(this.lineBuffer, state, observer);
            this.lineBuffer = '';
          }

          if (!observer.closed) {
            observer.complete();
          }
        } catch (err) {
          observer.error(err);
        }
      })();
    });
  }

  /** Invoke (non-streaming) response from backend. Used for non-English languages. */
  invokeResponse(body: any): Observable<any> {
    const url = this.buildApiUrl('/data/invokeChat');
    return this.http.post(url, body).pipe(
      timeout(120000),
      catchError(error => {
        if (error instanceof TimeoutError) {
          console.error('Invoke request timed out after 120s');
        }
        return throwError(() => error);
      })
    );
  }

  fetchWithToken(
    input: RequestInfo,
    init: RequestInit = {},
    scopes: string[] = [`api://${environment.backendId}/api.read.all`]
  ): Promise<Response> {
    const account = this.msalAuthService.instance.getActiveAccount();
    if (!account) {
      return Promise.reject(new Error('No active MSAL account found'));
    }

    return firstValueFrom(
      this.msalAuthService.acquireTokenSilent({ scopes, account })
    )
      .then(response => {
        const headers = new Headers(init.headers || {});
        headers.set('Authorization', `Bearer ${response.accessToken}`);

        return fetch(input, {
          ...init,
          headers
        });
      })
      .catch(error => {
        // Suppress expected abort noise when user interrupts streaming
        const isAbort = error && typeof error === 'object' && (error.name === 'AbortError' || error instanceof DOMException);
        if (!isAbort) {
          console.error('Error acquiring token silently:', error);
        }
        throw error;
      });
  }

  getChatProduct(name: string): ChatProduct {
    // Possible input values are:
    // Family from Selected System - dataApiName
    // Family from Chat Selection - instrumentName
    const defaultProduct: ChatProduct = {
      family: '1',
      apiversion: '1',
      botInformation: '1',
      aiSafetyInformation: '1'
    };

    // Guard: if config not yet loaded return default
    const config = this.appconfigService.current;
    if (!config || !Array.isArray(config) || config.length === 0) {
      return defaultProduct;
    }

    // Iterate with ability to early return on first match
    for (const item of config) {
      if (
        item['dataApiName'] === name ||
        item['instrumentName'] === name ||
        item['chatApiName'] === name
      ) {
        const product = {
          family: item['chatApiName'],
          apiversion: item['version'],
          botInformation: item['botInformation'],
          aiSafetyInformation: item['aiSafetyInformation']
        };
        return product; // early return ensures we don't overwrite with default later
      }
    }

    // No match found – return default
    return defaultProduct;
  }

  localizeMessageMetadata(messages: ChatMessage[]): ChatMessage[] {
    return messages.map((message) => {
      if (!message.family) {
        return message;
      }

      const product = this.getChatProduct(message.family);
      return {
        ...message,
        botInformation: message.botInformation !== undefined ? product.botInformation : message.botInformation,
        message_info: message.message_info !== undefined ? product.aiSafetyInformation : message.message_info,
      };
    });
  }

  private refreshLocalizedMessageMetadata(): void {
    if (this.messages.value.length === 0) {
      return;
    }

    this.messages.next(this.localizeMessageMetadata(this.messages.value));
  }

  addMessage(
    prompt: string,
    isPrePrompt: boolean,
    family?: string,
    isOptionAnswer?: boolean,
    transportDevice?: { material: string, serial: string }
  ) {
    //Increase Screensize for Readability
    if (this.widgetService.$workareaSize.value !== 'small' && this.widgetService.$workareaSize.value !== 'fullscreen') {
      this.widgetService.$workareaSize.next('medium');
    }

    //Enable Autoscroll
    this.autoscroll.next(true);

    //Check for Family / Instrument API
    let chatApi;
    let chatVersion;
    if (family) {
      chatApi = this.getChatProduct(family).family;
      chatVersion = this.getChatProduct(family).apiversion;
    } else if (
      !family &&
      this.selectedContentService.currentSystem?.['ProductFamily']
    ) {
      chatApi = this.getChatProduct(
        this.selectedContentService.currentSystem['ProductFamily']
      ).family;
      chatVersion = this.getChatProduct(
        this.selectedContentService.currentSystem['ProductFamily']
      ).apiversion;
    } else {
      chatApi = null;
      chatVersion = null;
    }

    //Create & persist user message via helper
    const entry = this.createUserMessage(
      prompt,
      isPrePrompt,
      chatApi,
      chatVersion,
      isOptionAnswer,
      transportDevice
    );
    this.messages.next([...this.messages.value, entry]);
    this.doAScroll.next();
    this.persistMessage(entry);

    const productFamilyForStream = this.resolveProductFamily();

    //Prepare Streaming Response (Delaying answer for 50 ms)
    setTimeout(() => {
      const shouldStartStreaming = !!productFamilyForStream;

      if (shouldStartStreaming) {
        this.isStreaming.next(true);
        this.resetStreamingState();
      }

      const botPlaceholder = this.createBotPlaceholderMessage(shouldStartStreaming);
      this.messages.next([...this.messages.value, botPlaceholder]);


      //If there is a product family available from Serial Number selection we can directly start the streaming
      if (productFamilyForStream) {


        // Resolve product just once to avoid multiple lookups and potential race conditions
        const chatProduct = this.getChatProduct(productFamilyForStream);
        const userMessages = this.messages.value.filter((x) => x.from === 'user');
        const lastUser = userMessages[userMessages.length - 1];
        const secondLastUser = userMessages[userMessages.length - 2];
        const effectiveQuery = lastUser?.isOptionAnswer && !secondLastUser?.family
          ? secondLastUser?.text
          : prompt;

        const body = {
          query: effectiveQuery,
          metadata: {
            lang: LANG_ISO_MAP[this.languageService.language.code] || 'en',
            reasoning_type: this.getReasoningType(),
            thread_id:
              this.messages.value[this.messages.value.length - 1]?.chatId ||
              this.historyService.generateUID(16),
            instrument: chatProduct.family,
            conversation_id: 'dummy',
            serial_no:
              this.selectedContentService.currentSystem?.SerialNumber ||
              null,
            user: this.authService.userData.basic['mail'],
            uuid:
              this.messages.value[this.messages.value.length - 1]?.uuid || null,
            version: chatProduct.apiversion,
            laboratory_id: this.selectedContentService.currentSite?.Id || null,
            data_api: environment.businessPartnerMode ? 'external' : 'internal'
          }
        };

        const isEnglish = this.languageService.language.code === 'en';

        if (isEnglish) {
          this.startStreamingRequest(body, chatProduct, productFamilyForStream);
        } else {
          this.startInvokeRequest(body, productFamilyForStream);
        }
      } else {
        //If there is no product family available from Serial Number selection we need to ask for the right family for the chat!
        this.setInstrumentSelectionOptions();
      }
    }, 50);
  }

  private startStreamingRequest(body: any, chatProduct: ChatProduct, productFamilyForStream: string): void {
    this.streamResponse(body, chatProduct.apiversion)
      .pipe(
        timeout(60000),
        catchError(error => {
          if (error instanceof TimeoutError) {
            console.error('Request timed out after 60s');
          }
          return throwError(() => error);
        }),
        observeOn(asyncScheduler)
      )
      .subscribe({
        next: (chunk: StreamState) => {
          this.enqueueChunk(chunk);
        },
        complete: () => {
          this.finalizeBotMessage(productFamilyForStream);
        },
        error: err => {
          const isAbort = err && typeof err === 'object' && (err.name === 'AbortError' || err instanceof DOMException);
          if (!this.wasInterruptedByUser && !isAbort) {
            console.error('Streaming error:', err);
          }

          if (this.wasInterruptedByUser || isAbort) {
            this.finalizeBotMessage(productFamilyForStream, true);
          } else {
            const msgs = [...this.messages.value];
            const lastMsg = msgs[msgs.length - 1];
            if (lastMsg && lastMsg.from === 'bot') {
              lastMsg.iStreaming = false;
              this.messages.next(msgs);
            }
            this.isStreaming.next(false);
            this.autoscroll.next(false);
            this.errorStreamingChat.next(true);
          }
          this.abortController = null;
          this.wasInterruptedByUser = false;
        }
      });
  }

  private startInvokeRequest(body: any, productFamilyForStream: string): void {
    // Pre-populate reasoning with "Thinking..." + no-details message for non-English
    const msgs = [...this.messages.value];
    const botMsg = msgs[msgs.length - 1];
    if (botMsg && botMsg.from === 'bot') {
      const thinkingLabel = this.translocoService.translate('chatbot.reasoning.thinking');
      const noDetails = this.translocoService.translate('chatbot.reasoning.noDetailsForLanguage');
      botMsg.reasoningTrace = [thinkingLabel];
      botMsg.reasoningSteps = [{ category: thinkingLabel, result: noDetails, resultLines: [noDetails] }];
      this.messages.next(msgs);
    }

    this.invokeSub = this.invokeResponse(body).subscribe({
      next: (response: any) => {
        const payload = this.extractResponsePayload(response);

        const updated = [...this.messages.value];
        const last = updated[updated.length - 1];

        if (last && last.from === 'bot') {
          last.text = payload.result || '';
          this.finalAnswer = typeof payload.final_answer === 'boolean' ? payload.final_answer : true;

          if (Array.isArray(payload.options) && payload.options.length) {
            last.options = payload.options;
          }

          const suggestions = payload.suggestions ?? response?.info?.suggestions;
          if (Array.isArray(suggestions) && suggestions.length) {
            last.suggestions = suggestions;
          }

          this.messages.next(updated);
          this.doAScroll.next();
        }

        this.finalizeBotMessage(productFamilyForStream);
      },
      error: err => {
        if (this.wasInterruptedByUser) {
          this.finalizeBotMessage(productFamilyForStream, true);
        } else {
          console.error('Invoke error:', err);
          const msgs = [...this.messages.value];
          const lastMsg = msgs[msgs.length - 1];
          if (lastMsg && lastMsg.from === 'bot') {
            lastMsg.iStreaming = false;
            this.messages.next(msgs);
          }
          this.isStreaming.next(false);
          this.autoscroll.next(false);
          this.errorStreamingChat.next(true);
        }
        this.invokeSub = null;
        this.wasInterruptedByUser = false;
      }
    });
  }

  public deleteChat(thread_id, version) {
    return this.http.post(
      window.location.protocol +
      '//' +
      window.location.hostname +
      (window.location.port !== '' ? ':' + '8080' : '') +
      '/data/deleteChatServer',
      { thread_id, version }
    );
  }

  public isStreaminginProgress() {
    if (this.isStreaming.getValue()) {
      const entry = {
        id: 'stream_in_progress',
        type: 'information',
        title: this.translocoService.translate('chatbot.streaming.inProgressTitle'),
        message: this.translocoService.translate('chatbot.streaming.inProgressMessage'),
        timestamp: new Date().toISOString(),
        read: false
      };
      const current = this.subscriptionService.$notifications.getValue();
      const currentCenter =
        this.subscriptionService.$notificationsCenter.getValue();
      this.subscriptionService.$notifications.next([...current, entry]);
      this.subscriptionService.$notificationsCenter.next([
        ...currentCenter,
        entry
      ]);
      return true;
    } else {
      return false;
    }
  }

  // Helper factories & utilities
  private buildApiUrl(path: string, version?: string): string {
    const base =
      window.location.protocol +
      '//' +
      window.location.hostname +
      (window.location.port !== '' ? ':' + '8080' : '');
    return base + path + (version || '');
  }

  private parseStreamLine(rawLine: string, state: StreamState, observer: any) {
    const line = rawLine.trim();
    if (!line) return;

    // Explicit SSE event lines
    if (line.toLowerCase().startsWith('event:')) {
      this.currentSseEvent = line.slice(6).trim().toLowerCase();
      return;
    }

    // Data lines corresponding to the last event
    if (line.startsWith('data:')) {
      const payload = line.slice(5).trim();
      if (!this.currentSseEvent) {
        this.processPotentialJson(payload, state, observer);
        return;
      }

      // 'end' should complete regardless of payload presence
      if (this.currentSseEvent === 'end') {
        observer.complete();
        this.currentSseEvent = null;
        return;
      }

      if (!payload) return; // ignore empty payloads for other events

      switch (this.currentSseEvent) {
        case 'response':
        case 'metadata':
          this.processPotentialJson(payload, state, observer);
          break;
        case 'reasoning':
          this.processReasoningJson(payload, observer);
          break;
        case 'info':
          try {
            const json = JSON.parse(payload);
            if (Array.isArray(json?.suggestions)) {
              state.suggestions = json.suggestions as string[];
              observer.next({
                result: '',
                final_answer: this.finalAnswer,
                options: [],
                suggestions: state.suggestions
              });
            }
          } catch (err) {
            console.warn('Failed to parse info JSON:', payload);
          }
          break;
        default:
          // Ignore unknown events for now
          break;
      }

      // Reset after handling the corresponding data line
      this.currentSseEvent = null;
      return;
    }
  }

  private createUserMessage(
    prompt: string,
    isPrePrompt: boolean,
    family?: string,
    chatVersion?: string,
    isOptionAnswer?: boolean,
    transportDevice?: { material: string, serial: string }
  ) {
    return {
      accountId: this.authService.userData.basic['id'],
      uuid: this.historyService.generateUID(24),
      chatId:
        this.messages.value[this.messages.value.length - 1]?.chatId ||
        this.historyService.generateUID(16),
      number: this.messages.value[this.messages.value.length - 1]?.number + 1 ||
        1,
      from: 'user',
      siteId: this.selectedContentService.currentSite?.Id || null,
      siteName: this.selectedContentService.currentSite?.Name || null,
      serial:
        this.selectedContentService.currentSystem?.['SerialNumber'] ||
        transportDevice?.serial ||
        NONE_DEVICE_SPECIFIC,
      material:
        this.selectedContentService.currentSystem?.['MaterialNumber'] ||
        transportDevice?.material ||
        NONE_DEVICE_SPECIFIC,
      family:
        family ||
        this.messages.value[this.messages.value.length - 1]?.family ||
        null,
      version:
        chatVersion ||
        this.messages.value[this.messages.value.length - 1]?.version ||
        null,
      botInformation: this.getChatProduct(
        family || this.messages.value[this.messages.value.length - 1]?.family || ''
      ).botInformation,
      text: prompt,
      timestamp: new Date().toISOString(),
      type: 'text',
      iStreaming: false,
      isPrePrompt: isPrePrompt,
      isOptionAnswer: isOptionAnswer || false,
      sessionid: this.sessionService.sessionId
    };
  }

  private createBotPlaceholderMessage(isStreaming: boolean = true) {
    return {
      accountId: this.authService.userData.basic['id'],
      uuid: this.messages.value[this.messages.value.length - 1]?.uuid || null,
      chatId:
        this.messages.value[this.messages.value.length - 1]?.chatId ||
        this.historyService.generateUID(16),
      number: this.messages.value[this.messages.value.length - 1]?.number + 1 ||
        1,
      from: 'bot',
      text: '',
      siteId: this.selectedContentService.currentSite?.Id || null,
      siteName: this.selectedContentService.currentSite?.Name || null,
      serial:
        this.selectedContentService.currentSystem?.['SerialNumber'] ||
        NONE_DEVICE_SPECIFIC,
      material:
        this.selectedContentService.currentSystem?.['MaterialNumber'] ||
        NONE_DEVICE_SPECIFIC,
      family: this.messages.value[this.messages.value.length - 1]?.family || null,
      version:
        this.messages.value[this.messages.value.length - 1]?.version || null,
      botInformation:
        this.messages.value[this.messages.value.length - 1]?.botInformation ||
        null,
      timestamp: new Date().toISOString(),
      type: 'text',
      iStreaming: isStreaming,
      isOptionAnswer: false,
      options: [],
      reasoningTrace: [],
      reasoningSteps: [],
      reasoningStartedAt: Date.now(),
      sessionid: this.sessionService.sessionId
    };
  }

  private persistMessage(entry: any) {
    this.historyService.addMessageArray(entry).subscribe({
      next: () => this.historyService.history.next(),
      error: (err) => {
        console.error('Error adding message to history:', err);
        this.errorSavingChat.next(true);
      }
    });
  }

  private resolveProductFamily(): string {
    return this.messages.value[this.messages.value.length - 1]?.family || null;
  }

  private finalizeBotMessage(productFamilyForStream: string, interruptedByUser: boolean = false) {
    const updated = [...this.messages.value];
    const last = updated[updated.length - 1];

    if (last && last.from === 'bot') {
      last.iStreaming = false;
      last.final_answer = this.finalAnswer;
      if (last.reasoningTrace?.length && last.reasoningStartedAt) {
        last.reasoningDurationMs = Date.now() - last.reasoningStartedAt;
        last.thoughtDurationLabel = this.formatThoughtDuration(last.reasoningDurationMs);
      }
      if (interruptedByUser) {
        last.interruptedByUser = true;
        last.interruptedLabel = this.translocoService.translate('chatbot.interrupted');
      }

      if (last.final_answer) {
        const prod = this.getChatProduct(productFamilyForStream);
        last.message_info = prod.aiSafetyInformation;
        // Clear options to unblock input — the message was already
        // persisted with options when they first arrived in enqueueChunk()
        last.options = [];
      }

      this.messages.next(updated);
      this.doAScroll.next();
      this.persistMessage(last);
    }

    this.isStreaming.next(false);
    this.autoscroll.next(false);
  }

  private formatThoughtDuration(durationMs: number): string {
    const safeDurationMs = Math.max(durationMs || 0, 1000);
    const seconds = Math.max(1, Math.round(safeDurationMs / 1000));
    if (seconds < 60) {
      const key = seconds === 1 ? 'chatbot.reasoning.thoughtForSeconds' : 'chatbot.reasoning.thoughtForSecondsPlural';
      return this.translocoService.translate(key, { count: seconds });
    }
    const minutes = Math.max(1, Math.round(seconds / 60));
    const key = minutes === 1 ? 'chatbot.reasoning.thoughtForMinutes' : 'chatbot.reasoning.thoughtForMinutesPlural';
    return this.translocoService.translate(key, { count: minutes });
  }

  private setInstrumentSelectionOptions() {
    const updated = [...this.messages.value];
    const last = updated[updated.length - 1];

    if (last) {
      last.from = 'bot-like';

      // Check if we have a site selected (not device, not none)
      const isSiteContext = this.selectedContentService.selection.kind === 'site';

      if (isSiteContext) {
        // Filter options to only available product families at the site
        const availableFamilies = this.selectedSiteService.$productFamilies.getValue();
        const filteredOptions = (this.appconfigService.current || [])
          .filter(item => availableFamilies.includes(item['dataApiName']))
          .map(item => item['instrumentName']) || [];

        if (filteredOptions.length > 0) {
          last.text += this.translocoService.translate('chatbot.instrumentSelection.sitePrompt');
          last.options = filteredOptions;
        } else {
          last.text += this.translocoService.translate('chatbot.instrumentSelection.noSystems');
          last.options = [];
        }
      } else {
        last.text += this.translocoService.translate('chatbot.instrumentSelection.prompt');
        last.options = (this.appconfigService.current || []).map(
          item => item['instrumentName']
        ) || [];
      }

      this.messages.next(updated);
      this.persistMessage(last);
    }

    this.isStreaming.next(false);
  }

  // --- Direct token append (no typing animation) ---
  private enqueueChunk(chunk: StreamState) {
    if (!chunk.result && !chunk.reasoningCategory && !chunk.reasoningResult && !chunk.suggestions?.length && !chunk.options?.length) {
      return;
    }

    const updated = [...this.messages.value];
    const last = updated[updated.length - 1];

    if (last && last.from === 'bot') {
      this.appendReasoningChunk(last, chunk);

      if (chunk.result) {
        last.text += chunk.result;
      }

      if (chunk.options && chunk.options.length) {
        last.options = chunk.options;
        last.iStreaming = false;
        this.isStreaming.next(false);
        this.autoscroll.next(false);
        this.persistMessage(last);
      }

      if (Array.isArray(chunk.suggestions) && chunk.suggestions.length) {
        last.suggestions = chunk.suggestions;
      }

      this.messages.next(updated);
      this.doAScroll.next();
    }
  }

  private resetStreamingState() {
    this.finalAnswer = false;
  }

  private getReasoningType(): 'think' | 'debug' {
    return environment.production ? 'think' : 'debug';
  }

  /** Public method to interrupt an active stream immediately. */
  interruptStreaming(): void {
    if (!this.isStreaming.getValue()) {
      return; // nothing to interrupt
    }
    // Mark as user-interrupted and abort the request
    this.wasInterruptedByUser = true;
    if (this.abortController) {
      try {
        this.abortController.abort();
      } catch {
        // ignore abort errors
      }
    }
    // Also cancel invoke request if active
    if (this.invokeSub) {
      this.invokeSub.unsubscribe();
      this.invokeSub = null;
      this.finalizeBotMessage(this.resolveProductFamily(), true);
    }
  }

  private processPotentialJson(jsonPart: string, state: StreamState, observer: any) {
    try {
      const json = JSON.parse(jsonPart);
      const payload = this.extractResponsePayload(json);
      const reasoningEntries = this.extractReasoningEntries(json);

      reasoningEntries.forEach((entry) => {
        observer.next({
          result: '',
          final_answer: this.finalAnswer,
          options: state.options || [],
          reasoningCategory: entry.category,
          reasoningResult: entry.result
        });
      });

      Object.assign(state, payload);

      if (typeof payload.final_answer === 'boolean') {
        this.finalAnswer = payload.final_answer;
      }

      if (Array.isArray(payload.options)) {
        state.options = payload.options;
      }

      if (payload.result) {
        observer.next({
          result: payload.result,
          final_answer: this.finalAnswer,
          options: state.options || []
        });
      } else if (payload.final_answer) {
        // final flag without additional text; nothing special to do here,
        // finalize will happen on stream completion
      }
    } catch (err) {
      console.warn('Failed to parse JSON stream part:', jsonPart);
    }
  }

  private processReasoningJson(jsonPart: string, observer: any) {
    try {
      const json = JSON.parse(jsonPart);

      const reasoningEntries = this.extractReasoningEntries(json, true);

      if (reasoningEntries.length) {
        reasoningEntries.forEach((entry) => {
          observer.next({
            result: '',
            final_answer: this.finalAnswer,
            options: [],
            reasoningCategory: entry.category,
            reasoningResult: entry.result
          });
        });
      }
    } catch (err) {
      console.warn('Failed to parse reasoning JSON:', jsonPart);
    }
  }

  private extractResponsePayload(json: any) {
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
      return {};
    }

    if (json.response && typeof json.response === 'object' && !Array.isArray(json.response)) {
      const nestedResponse = this.extractResponsePayload(json.response);
      if (Object.keys(nestedResponse).length > 0) {
        return this.mergeResponseMetadata(nestedResponse, json.metadata);
      }
    }

    if (json.data && typeof json.data === 'object' && !Array.isArray(json.data)) {
      const nestedData = this.extractResponsePayload(json.data);
      if (Object.keys(nestedData).length > 0) {
        return this.mergeResponseMetadata(nestedData, json.metadata);
      }
    }

    if (this.hasResponseFields(json)) {
      return this.mergeResponseMetadata(json, json.metadata);
    }

    return {};
  }

  private mergeResponseMetadata(payload: any, metadata: any) {
    if (typeof metadata?.final_answer !== 'boolean' || typeof payload?.final_answer === 'boolean') {
      return payload;
    }

    return {
      ...payload,
      final_answer: metadata.final_answer
    };
  }

  private extractReasoningEntries(payload: any, allowCurrentNode: boolean = false): Array<{ category?: string; result?: string }> {
    if (!payload || typeof payload !== 'object') {
      return [];
    }

    if (Array.isArray(payload)) {
      return payload.flatMap((item) => this.extractReasoningEntries(item, allowCurrentNode));
    }

    const entries: Array<{ category?: string; result?: string }> = [];
    const category = typeof payload.category === 'string' && payload.category.trim()
      ? payload.category.trim()
      : undefined;
    const result = typeof payload.result === 'string' && payload.result.trim()
      ? payload.result
      : undefined;

    if (allowCurrentNode && (category || result)) {
      entries.push({ category, result });
    }

    if (payload.reasoning) {
      entries.push(...this.extractReasoningEntries(payload.reasoning, true));
    }

    if (payload.response && typeof payload.response === 'object') {
      entries.push(...this.extractReasoningEntries(payload.response, false));
    }

    if (payload.data && typeof payload.data === 'object') {
      entries.push(...this.extractReasoningEntries(payload.data, false));
    }

    return entries;
  }

  private hasResponseFields(payload: any): boolean {
    return typeof payload?.result === 'string'
      || typeof payload?.final_answer === 'boolean'
      || Array.isArray(payload?.options);
  }

  private appendReasoningChunk(last: ChatMessage, chunk: StreamState) {
    if (!chunk.reasoningCategory && !chunk.reasoningResult) {
      return;
    }

    const category = chunk.reasoningCategory?.trim();
    const result = chunk.reasoningResult;

    if (category && last.reasoningTrace?.[last.reasoningTrace.length - 1] !== category) {
      last.reasoningTrace = [...(last.reasoningTrace || []), category];
    }

    const steps = [...(last.reasoningSteps || [])];
    const previousStep = steps[steps.length - 1];

    if (!previousStep) {
      steps.push({
        category,
        result: result || '',
        resultLines: result ? [result] : []
      });
      last.reasoningSteps = steps;
      return;
    }

    if (result && (!category || previousStep.category === category)) {
      const existingLines = previousStep.resultLines?.length
        ? [...previousStep.resultLines]
        : (previousStep.result ? [previousStep.result] : []);

      existingLines.push(result);
      previousStep.resultLines = existingLines;
      previousStep.result = existingLines.join('\n');

      if (category && !previousStep.category) {
        previousStep.category = category;
      }
      last.reasoningSteps = steps;
      return;
    }

    if (category && previousStep.category === category && !result) {
      last.reasoningSteps = steps;
      return;
    }

    steps.push({
      category,
      result: result || '',
      resultLines: result ? [result] : []
    });
    last.reasoningSteps = steps;
  }

}